import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const existingToken = useAuthStore((s) => s.token);

  // All hooks must be called before any early return (React rules of hooks)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already authenticated, redirect to dashboard
  if (existingToken) {
    return <Navigate to="/app" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Email lookup is normalized to lowercase on the backend, but do it here too
      // so the value sent + stored + displayed all agree. Prevents "sporadic 401 out
      // of nowhere" when a user types their email with any capital letter.
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedSlug = tenantSlug.trim().toLowerCase();

      // Clear any stale auth state before login
      const store = useAuthStore.getState();
      if (store.token) {
        store.setTokens('', '');
      }
      store.setTenantSlug(trimmedSlug);

      // Use raw fetch instead of axios to bypass any interceptor issues
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Slug': trimmedSlug,
        },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        console.error('[LOGIN] Failed:', res.status, errBody);
        throw new Error(errBody?.detail || 'Login failed');
      }

      const data = await res.json();
      // Decode user info from token (simplified — in production use /users/me)
      const payload = JSON.parse(atob(data.access_token.split('.')[1]));
      setAuth(
        {
          id: payload.sub,
          email: payload.email,
          full_name: payload.email,
          role: payload.role,
          preferred_locale: 'fr',
          organization_id: payload.organization_id ?? null,
        },
        data.access_token,
        data.refresh_token,
        trimmedSlug,
      );
      navigate('/app');
    } catch {
      setError(t('common:loginError', 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
        <h1 className={styles.title}>QENQ</h1>
        <p className={styles.subtitle}>
          {t('common:loginSubtitle', 'Urban Development Cost Estimation')}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.field}>
          <label>{t('common:tenant', 'Organization')}</label>
          <input
            type="text"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            placeholder="your-organization"
            required
            autoComplete="off"
          />
        </div>

        <div className={styles.field}>
          <label>{t('common:email', 'Email')}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
            autoComplete="off"
          />
        </div>

        <div className={styles.field}>
          <label>{t('common:password', 'Password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="********"
            required
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? '...' : t('common:login', 'Login')}
        </button>

        <Link to="/forgot-password" className={styles.linkButton}>
          {t('common:forgotPassword', 'Forgot password?')}
        </Link>
      </form>
    </div>
  );
}
